import {
    ActivityTrackerUtils,
    DateService,
    DebugControlsService,
    DeviceIdService,
    DocumentVisibilityTracker,
    HashStore,
    HttpService,
    IndexedDBService,
    LockManager,
    LockManagerToken,
    LoggerService,
    PageInfoService,
    PulseEnvironment,
    Quote,
    RouteHelperService,
    StateService,
    StorageSelector,
    StorageSelectorToken,
    TrafficSourceService,
    TransientStorage,
    UTMTrackerService,
    UUIDService,
    UnPrefixedCookieStorageToken,
    WebToMobileService,
} from '@pulse/browser-shared';
import { PluginHost } from '@pulse/browser-tracker-shared';
import {
    ProviderBuilder,
    PulseRootConfigurationToken,
    StorageChangeOrigin,
    TrackerConfigurations,
} from '@pulse/common';
import { PipeHost } from '../pipe';
import {
    ActionParamEventPipe,
    AttributeSpreadPipe,
    ChangedAttributesPipe,
    EventGlobalsPipe,
    LegacySourcePipe,
    NavigationEventPipe,
} from '../pipes';
import {
    ConsentEventPlugin,
    DeviceAttributesPlugin,
    FacebookPixelAttributePlugin,
    NavigationEventPlugin,
    PopupEventPlugin,
    SessionStartEventPlugin,
    StatePlugin,
    VisibilityChangePlugin,
} from '../plugins';
import { ClickEventPlugin } from '../plugins/builtin-events/click-event.plugin';
import { ContextMenuPlugin } from '../plugins/builtin-events/context-menu.plugin';
import { InfectedPlugin } from '../plugins/infected.plugin';
import { DebugEventPlugin } from '../plugins/live-event-debug.plugin';
import { TabLinkingPlugin } from '../plugins/tab-linking.plugin';
import {
    ActivitySynchronizer,
    ActivityTracker,
    AttributesCacheService,
    DebugService,
    DeviceAttributesService,
    DeviceInfoService,
    ExperimentsService,
    LegacySourceService,
    LocationHistoryTracker,
    NavigationEventService,
    OneTrustConsentService,
    PulseTransport,
    SessionService,
    TabInfoService,
    TabManagerService,
    TabOpenerDetector,
    TrackingConsentService,
} from '../services';
import { LiveEventDebugService } from '../services/live-event-debug.service';
import { PulseTracker } from '../tracker';
import { BatchQueue, MessageQueue } from '../transport';
import { PipesFactory } from './pipes';
import { PluginsFactory } from './plugins';
import {
    DataLayerToken,
    DefaultPipesHostToken,
    InternalDataLayerToken,
    PipesHostToken,
} from './tokens';

//

export function trackerProviders(builder: ProviderBuilder) {
    return (
        builder
            .useFactory(TrackerConfigurations, (config) => config, [PulseRootConfigurationToken])
            //
            // Data Layers
            ////////////////////////////
            .useFactory(
                DataLayerToken,
                (env: PulseEnvironment, config: TrackerConfigurations) =>
                    (env.win as any)[config.dataLayerKey],
                [PulseEnvironment, TrackerConfigurations],
            )
            .useFactory(
                InternalDataLayerToken,
                (env: PulseEnvironment) => (env.win as any).pulse?.q,
                [PulseEnvironment],
            )
            //
            // Consent
            ////////////////////////////
            .useClass(OneTrustConsentService, OneTrustConsentService, [
                UnPrefixedCookieStorageToken,
            ])
            .useClass(TrackingConsentService, TrackingConsentService, [
                OneTrustConsentService,
                LoggerService,
                HashStore,
                TrackerConfigurations,
            ])

            // Trackers
            ////////////////////////////
            .useClass(LegacySourceService, LegacySourceService, [
                UTMTrackerService,
                PageInfoService,
            ])

            //
            // Session/Transport
            ////////////////////////////
            .useFactory(
                SessionService,
                (
                    date: DateService,
                    getStorage: StorageSelector,
                    trafficSourceService: TrafficSourceService,
                    hashStore: HashStore,
                    config: TrackerConfigurations,
                    deviceIdService: DeviceIdService,
                    debugService: DebugService,
                    webToMobileService: WebToMobileService,
                    stateService: StateService,
                ) =>
                    new SessionService(
                        date,
                        getStorage(config.sessionStorageType),
                        trafficSourceService,
                        hashStore,
                        config,
                        deviceIdService,
                        debugService,
                        webToMobileService,
                        stateService,
                        StorageChangeOrigin.All,
                    ),
                [
                    DateService,
                    StorageSelectorToken,
                    TrafficSourceService,
                    HashStore,
                    TrackerConfigurations,
                    DeviceIdService,
                    DebugService,
                    WebToMobileService,
                    StateService,
                ],
            )
            .useClass(PulseTransport, PulseTransport, [
                DebugService,
                TrackerConfigurations,
                HttpService,
            ])
            .useFactory(
                LocationHistoryTracker,
                async (
                    env: PulseEnvironment,
                    selectStorage: StorageSelector,
                    config: TrackerConfigurations,
                    dateService: DateService,
                ) =>
                    new LocationHistoryTracker(
                        env,
                        selectStorage(config.tabManagerStorageType),
                        dateService,
                    ),
                [PulseEnvironment, StorageSelectorToken, TrackerConfigurations, DateService],
            )
            .useClass(LiveEventDebugService, LiveEventDebugService, [
                DebugService,
                DebugControlsService,
                TrackerConfigurations,
                DeviceIdService,
            ])
            .useFactory(
                DebugService,
                (selectStorage: StorageSelector, config: TrackerConfigurations) =>
                    new DebugService(selectStorage(config.sessionStorageType), config),
                [StorageSelectorToken, TrackerConfigurations],
            )
            .useFactory(
                NavigationEventService,
                (tabOpener: TabOpenerDetector, env: PulseEnvironment) =>
                    new NavigationEventService(tabOpener, env),
                [TabOpenerDetector, PulseEnvironment],
            )
            .useClass(MessageQueue, MessageQueue, [
                TrackingConsentService,
                TrackerConfigurations,
                BatchQueue,
                DocumentVisibilityTracker,
            ])
            .useFactory(
                ExperimentsService,
                (
                    getStorage: StorageSelector,
                    config: TrackerConfigurations,
                    stateService: StateService,
                ) => new ExperimentsService(getStorage('persistent'), config, stateService),
                [StorageSelectorToken, TrackerConfigurations, StateService],
            )
            .useClass(AttributesCacheService, AttributesCacheService, [
                IndexedDBService,
                Quote,
                DeviceIdService,
                DateService,
            ])
            .useClass(DeviceInfoService, DeviceInfoService, [PulseEnvironment])
            .useFactory(
                BatchQueue,
                (
                    getStorage: StorageSelector,
                    lockManager: LockManager,
                    uuid: UUIDService,
                    visibility: DocumentVisibilityTracker,
                    consentService: TrackingConsentService,
                    transport: PulseTransport,
                    config: TrackerConfigurations,
                ) =>
                    new BatchQueue(
                        getStorage('persistent'),
                        lockManager,
                        uuid,
                        visibility,
                        consentService,
                        transport,
                        config,
                    ),
                [
                    StorageSelectorToken,
                    LockManagerToken,
                    UUIDService,
                    DocumentVisibilityTracker,
                    TrackingConsentService,
                    PulseTransport,
                    TrackerConfigurations,
                ],
            )
            .useFactory(
                DeviceAttributesService,
                (
                    deviceInfoService: DeviceInfoService,
                    utmTrackerService: UTMTrackerService,
                    getStorage: StorageSelector,
                    dateService: DateService,
                    config: TrackerConfigurations,
                    sessionService: SessionService,
                    batchQueue: BatchQueue,
                    env: PulseEnvironment,
                ) =>
                    new DeviceAttributesService(
                        deviceInfoService,
                        utmTrackerService,
                        getStorage('persistent'),
                        dateService,
                        config,
                        sessionService,
                        batchQueue,
                        env,
                    ),
                [
                    DeviceInfoService,
                    UTMTrackerService,
                    StorageSelectorToken,
                    DateService,
                    TrackerConfigurations,
                    SessionService,
                    BatchQueue,
                    PulseEnvironment,
                ],
            )

            //
            // Pipe/Plugin Controller
            ////////////////////////////
            .useClass(PluginHost, PluginHost, [])
            .useClass(PipesHostToken, PipeHost, [])
            .useClass(DefaultPipesHostToken, PipeHost, [])

            //
            // Pipes
            ////////////////////////////
            .useClass(AttributeSpreadPipe, AttributeSpreadPipe, [])
            .useClass(ActionParamEventPipe, ActionParamEventPipe, [])
            .useClass(NavigationEventPipe, NavigationEventPipe, [PageInfoService])
            .useClass(ChangedAttributesPipe, ChangedAttributesPipe, [
                HashStore,
                TrackerConfigurations,
            ])
            .useClass(EventGlobalsPipe, EventGlobalsPipe, [
                LocationHistoryTracker,
                TabManagerService,
                PageInfoService,
                TrackerConfigurations,
                TabOpenerDetector,
                UUIDService,
                PulseEnvironment,
                WebToMobileService,
            ])
            .useClass(LegacySourcePipe, LegacySourcePipe, [
                LegacySourceService,
                TrackerConfigurations,
            ])

            //
            // Plugins
            ////////////////////////////
            .useClass(VisibilityChangePlugin, VisibilityChangePlugin, [DocumentVisibilityTracker])
            .useClass(ClickEventPlugin, ClickEventPlugin, [ActivityTracker, PulseEnvironment])
            .useClass(TabLinkingPlugin, TabLinkingPlugin, [
                ActivitySynchronizer,
                TabInfoService,
                TabManagerService,
                TrackerConfigurations,
            ])
            .useClass(NavigationEventPlugin, NavigationEventPlugin, [
                LocationHistoryTracker,
                NavigationEventService,
                SessionService,
                TrackingConsentService,
            ])
            .useClass(InfectedPlugin, InfectedPlugin, [ExperimentsService])
            .useClass(StatePlugin, StatePlugin, [
                SessionService,
                ExperimentsService,
                DebugService,
                TrackerConfigurations,
                DeviceInfoService,
                StateService,
            ])
            .useClass(VisibilityChangePlugin, VisibilityChangePlugin, [DocumentVisibilityTracker])
            .useClass(PopupEventPlugin, PopupEventPlugin, [ActivityTracker, PulseEnvironment])
            .useClass(DeviceAttributesPlugin, DeviceAttributesPlugin, [DeviceAttributesService])
            .useClass(ConsentEventPlugin, ConsentEventPlugin, [
                TrackingConsentService,
                SessionService,
                DeviceAttributesService,
                PulseEnvironment,
            ])
            .useClass(FacebookPixelAttributePlugin, FacebookPixelAttributePlugin, [
                UnPrefixedCookieStorageToken,
                HashStore,
                DeviceAttributesService,
                PulseEnvironment,
            ])
            .useClass(SessionStartEventPlugin, SessionStartEventPlugin, [
                HashStore,
                TrafficSourceService,
                SessionService,
                PulseEnvironment,
                WebToMobileService,
            ])
            .useClass(ContextMenuPlugin, ContextMenuPlugin, [ActivityTracker, PulseEnvironment])
            .useClass(DebugEventPlugin, DebugEventPlugin, [
                DebugService,
                PulseEnvironment,
                LiveEventDebugService,
                TrackerConfigurations,
                RouteHelperService,
                WebToMobileService,
            ])

            //
            // Factories
            ////////////////////////////
            .useClass(PluginsFactory, PluginsFactory, [
                SessionStartEventPlugin,
                NavigationEventPlugin,
                InfectedPlugin,
                VisibilityChangePlugin,
                ClickEventPlugin,
                TabLinkingPlugin,
                StatePlugin,
                DeviceAttributesPlugin,
                ConsentEventPlugin,
                PluginHost,
                TrackerConfigurations,
                FacebookPixelAttributePlugin,
                PopupEventPlugin,
                ContextMenuPlugin,
                InternalDataLayerToken,
                DebugEventPlugin,
                DataLayerToken,
            ])
            .useClass(PipesFactory, PipesFactory, [
                AttributeSpreadPipe,
                ActionParamEventPipe,
                NavigationEventPipe,
                ChangedAttributesPipe,
                EventGlobalsPipe,
                LegacySourcePipe,
                PipesHostToken,
                DefaultPipesHostToken,
                TrackerConfigurations,
            ])

            //
            // Tabs
            ////////////////////////////
            .useFactory(
                TabManagerService,
                async (
                    selectStorage: StorageSelector,
                    transientStorage: TransientStorage,
                    lock: LockManager,
                    uuid: UUIDService,
                    quote: Quote,
                    env: PulseEnvironment,
                    config: TrackerConfigurations,
                ) => {
                    const tabManager = new TabManagerService(
                        selectStorage(config.tabManagerStorageType),
                        transientStorage,
                        lock,
                        uuid,
                        quote,
                        env,
                        config,
                    );
                    await tabManager.init();

                    return tabManager;
                },
                [
                    StorageSelectorToken,
                    TransientStorage,
                    LockManagerToken,
                    UUIDService,
                    Quote,
                    PulseEnvironment,
                    TrackerConfigurations,
                ],
            )
            .useFactory(
                TabInfoService,
                (
                    selectStorage: StorageSelector,
                    visibilityService: DocumentVisibilityTracker,
                    activityTracker: ActivityTracker,
                    activitySynchronizer: ActivitySynchronizer,
                    date: DateService,
                    config: TrackerConfigurations,
                ) =>
                    new TabInfoService(
                        selectStorage(config.sessionStorageType),
                        visibilityService,
                        activityTracker,
                        activitySynchronizer,
                        date,
                        config,
                    ),
                [
                    StorageSelectorToken,
                    DocumentVisibilityTracker,
                    ActivityTracker,
                    ActivitySynchronizer,
                    DateService,
                    TrackerConfigurations,
                ],
            )
            .useFactory(
                TabOpenerDetector,
                (
                    tabInfoService: TabInfoService,
                    tabStorage: StorageSelector,
                    activitySynchronizer: ActivitySynchronizer,
                    tabManager: TabManagerService,
                    date: DateService,
                    env: PulseEnvironment,
                    config: TrackerConfigurations,
                ) => {
                    const tabOpener = new TabOpenerDetector(
                        tabInfoService,
                        tabStorage(config.tabManagerStorageType),
                        activitySynchronizer,
                        tabManager,
                        date,
                        env,
                        config,
                    );

                    return tabOpener;
                },
                [
                    TabInfoService,
                    StorageSelectorToken,
                    ActivitySynchronizer,
                    TabManagerService,
                    DateService,
                    PulseEnvironment,
                    TrackerConfigurations,
                ],
            )

            //
            // Trackers
            ////////////////////////////
            .useFactory(
                ActivitySynchronizer,
                (
                    getStorage: StorageSelector,
                    activityTracker: ActivityTracker,
                    dateService: DateService,
                    config: TrackerConfigurations,
                ) =>
                    new ActivitySynchronizer(
                        getStorage('persistent'),
                        activityTracker,
                        dateService,
                        config,
                    ),
                [StorageSelectorToken, ActivityTracker, DateService, TrackerConfigurations],
            )
            .useClass(ActivityTracker, ActivityTracker, [
                DocumentVisibilityTracker,
                PulseEnvironment,
                TabManagerService,
                TrackerConfigurations,
                ActivityTrackerUtils,
            ])

            //
            // Tracker
            ////////////////////////////
            //
            // Tracker
            ////////////////////////////
            .useFactory(
                PulseTracker,
                (
                    plugins: PluginHost,
                    pipes: PipeHost,
                    defaultPipes: PipeHost,
                    logger: LoggerService,
                    queue: MessageQueue,
                    sessionService: SessionService,
                    date: DateService,
                    config: TrackerConfigurations,
                    pipesFactory: PipesFactory,
                    pluginsFactory: PluginsFactory,
                    tabManagerService: TabManagerService,
                    stateService: StateService,
                ) => {
                    pipesFactory.registerPipes();
                    pluginsFactory.registerPlugins();

                    const pulse = new PulseTracker(
                        plugins,
                        pipes,
                        logger,
                        defaultPipes,
                        queue,
                        sessionService,
                        tabManagerService,
                        date,
                        config,
                        stateService,
                    );

                    return pulse;
                },
                [
                    PluginHost,
                    PipesHostToken,
                    DefaultPipesHostToken,
                    LoggerService,
                    MessageQueue,
                    SessionService,
                    DateService,
                    TrackerConfigurations,
                    PipesFactory,
                    PluginsFactory,
                    TabManagerService,
                    StateService,
                ],
            )
    );
}
